import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { PushNotificationsToggle } from "../components/PushNotificationsToggle";
import {
  Bell,
  CheckCircle2,
  CalendarDays,
  Users2,
  DollarSign,
  ClipboardList,
  PlayCircle,
  AlertTriangle,
  Inbox,
} from "lucide-react";
import { fmtDateTime, brl } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";

// Map a heuristic event type → icon + tint based on the notification message
function inferType(message) {
  const m = (message || "").toLowerCase();
  if (m.includes("finalizada") || m.includes("concluí") || m.includes("conclu"))
    return { icon: CheckCircle2, color: "emerald", labelKey: "Limpeza finalizada" };
  if (m.includes("iniciou") || m.includes("start") || m.includes("começou"))
    return { icon: PlayCircle, color: "blue", labelKey: "Limpeza iniciada" };
  if (m.includes("agendamento") || m.includes("solicit") || m.includes("booking"))
    return { icon: ClipboardList, color: "indigo", labelKey: "Nova solicitação" };
  if (m.includes("check-in") || m.includes("checkin") || m.includes("chegou"))
    return { icon: Users2, color: "violet", labelKey: "Check-in colaborador" };
  if (m.includes("pagamento") || m.includes("paid") || m.includes("recebido") || m.includes("payment"))
    return { icon: DollarSign, color: "emerald", labelKey: "Pagamento" };
  if (m.includes("atrasada") || m.includes("vencid") || m.includes("alerta"))
    return { icon: AlertTriangle, color: "amber", labelKey: "Alerta" };
  return { icon: Bell, color: "slate", labelKey: "Notificação" };
}

// Group items by day (today/yesterday/older)
function groupByDay(items) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const groups = { Hoje: [], Ontem: [], Anteriores: [] };
  for (const it of items) {
    const d = new Date(it.created_at);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) groups["Hoje"].push(it);
    else if (d.getTime() === yesterday.getTime()) groups["Ontem"].push(it);
    else groups["Anteriores"].push(it);
  }
  return groups;
}

export default function Notifications() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function mark(id) {
    await api.post(`/notifications/${id}/read`);
    load();
  }
  async function markAll() {
    await Promise.all(
      items.filter((n) => !n.read).map((n) => api.post(`/notifications/${n.id}/read`)),
    );
    load();
  }

  const unread = items.filter((n) => !n.read);
  const visible = filter === "unread" ? unread : items;
  const grouped = groupByDay(visible);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Comunicação")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Bell size={26} /> {t("Notificações")}
            {unread.length > 0 && (
              <span className="text-sm font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                {unread.length} {t("sem ler")}
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t("Eventos do sistema: agendamentos, check-ins, pagamentos, limpezas finalizadas e alertas.")}
          </p>
          <div className="mt-2">
            <PushNotificationsToggle />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-1 text-xs">
            <button
              data-testid="notif-filter-all"
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 rounded-md font-semibold ${
                filter === "all" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"
              }`}
            >
              {t("Todas")}
            </button>
            <button
              data-testid="notif-filter-unread"
              onClick={() => setFilter("unread")}
              className={`px-3 py-1.5 rounded-md font-semibold ${
                filter === "unread" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"
              }`}
            >
              {t("Sem ler")}
            </button>
          </div>
          {unread.length > 0 && (
            <button
              data-testid="notif-mark-all"
              onClick={markAll}
              className="px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 rounded-lg border border-blue-200"
            >
              {t("Marcar todas como lidas")}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="cm-card p-10 text-center text-slate-500">{t("Carregando…")}</div>
      ) : visible.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([label, list]) =>
            list.length === 0 ? null : (
              <div key={label}>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 px-1">
                  {t(label)}
                </div>
                <div className="cm-card p-0 overflow-hidden">
                  {list.map((n) => (
                    <NotifRow key={n.id} n={n} onMark={() => mark(n.id)} />
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function NotifRow({ n, onMark }) {
  const { t } = useI18n();
  const meta = inferType(n.message);
  const Icon = meta.icon;
  const tint = {
    emerald: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    indigo: "bg-indigo-100 text-indigo-700",
    violet: "bg-violet-100 text-violet-700",
    amber: "bg-amber-100 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  }[meta.color];

  const linkMatch = (n.message || "").match(/\/(jobs|clients|recebimentos|finance|hoje|booking-requests)\/?[\w-]*/);
  const deepLink = linkMatch ? linkMatch[0] : null;

  return (
    <div
      data-testid={`notification-${n.id}`}
      className={`flex items-start gap-3 p-4 border-b border-slate-100 last:border-0 transition-colors ${
        n.read ? "opacity-60" : "bg-blue-50/30"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tint}`}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {t(meta.labelKey)}
          </span>
          {!n.read && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">
              {t("Novo")}
            </span>
          )}
        </div>
        <div className="font-medium text-slate-900 mt-0.5">{n.message}</div>
        <div className="text-xs text-slate-500 mt-1">{fmtDateTime(n.created_at)}</div>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {deepLink && (
          <Link
            to={deepLink}
            data-testid={`notification-link-${n.id}`}
            className="text-xs font-semibold text-blue-700 hover:underline"
          >
            {t("Ver →")}
          </Link>
        )}
        {!n.read && (
          <button
            onClick={onMark}
            data-testid={`notification-read-${n.id}`}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            <CheckCircle2 size={12} /> {t("Marcar lido")}
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ filter }) {
  const { t } = useI18n();
  return (
    <div className="cm-card p-10 text-center">
      <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
        <Inbox size={28} />
      </div>
      <div className="font-semibold text-slate-700">
        {filter === "unread"
          ? t("Tudo em dia! Nenhuma notificação não lida.")
          : t("Sua caixa de entrada está vazia")}
      </div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {t("Aqui aparecem os eventos do sistema: novos agendamentos, check-ins, pagamentos recebidos, limpezas finalizadas e alertas operacionais.")}
      </p>
    </div>
  );
}
